<template>
    <div class="dealers-app__card--dealer-group">
        <!-- Primary dealer location -->
        <button 
            class="dealers-app__card--dealer" 
            @click="setSelectedDealer(dealerGroup.primaryLocation)"
            :class="{ 'active': isLocationSelected(dealerGroup.primaryLocation), 'online-retailer': dealerGroup.primaryLocation?.onlineRetailer }"
        >
            <h3 class="dealers-app__card--name">{{ dealerGroup?.dealerName || 'Unknown Dealer' }}</h3>
            <div v-if="dealerGroup.primaryLocation?.onlineRetailer" class="dealers-app__card--online-badge">
                <span>
                    <Earth size="20" color="#ff7f33" />
                    Online Retailer
                </span>
            </div>
            <p v-if="dealerGroup.primaryLocation?.location?.name" class="dealers-app__card--location-name">
                {{ dealerGroup.primaryLocation.location.name }}
            </p>
            <span v-if="!dealerGroup.primaryLocation?.onlineRetailer" class="distance-badge">
                <MapPin size="18"/>
                {{ dealerGroup.primaryLocation?.distance ? Math.round(dealerGroup.primaryLocation.distance * 10) / 10 : 'N/A' }} miles away
            </span>
            <div v-if="dealerGroup.primaryLocation?.distance !== null && dealerGroup.primaryLocation?.distance !== undefined" class="dealers-app__card--distance">
                <p class="dealers-app__card--address">
                    <Map color="#101921"/>
                    {{ getPrimaryLocationAddress() }}
                </p>
            </div>
        </button>

        <!-- Collapsible section for additional locations -->
        <div v-if="dealerGroup.hasMultipleLocations" class="dealers-app__card--additional-locations">
            <button 
                class="dealers-app__card--toggle-locations" 
                @click="toggleAdditionalLocations"
                type="button"
                :aria-expanded="showAdditionalLocations"
                :aria-controls="`additional-locations-${dealerGroup.dealerId}`"
            >
                <ChevronDown 
                    :class="{ 'rotated': showAdditionalLocations }" 
                    size="16" 
                />
                {{ showAdditionalLocations ? 'Hide' : 'Show' }} more locations ({{ dealerGroup.additionalLocations.length }})
            </button>
            
            <transition name="slide-fade">
                <div 
                    v-if="showAdditionalLocations" 
                    class="dealers-app__card--locations-list"
                    :id="`additional-locations-${dealerGroup.dealerId}`"
                >
                    <button
                        v-for="location in dealerGroup.additionalLocations"
                        :key="`${location.dealerId}-${location.location.name}`"
                        class="dealers-app__card--additional-location"
                        @click="setSelectedDealer(location)"
                        :class="{ 'active': isLocationSelected(location) }"
                    >
                        <p v-if="location?.location?.name" class="dealers-app__card--additional-location-name">
                            {{ location.location.name }}
                        </p>
                        <span class="distance-badge">
                            <MapPin size="14"/>
                            {{ location?.distance ? Math.round(location.distance * 10) / 10 : 'N/A' }} miles away
                        </span>
                        <p class="dealers-app__card--address">
                            <Map color="#101921"/>
                            {{ getLocationAddress(location) }}
                        </p>
                    </button>
                </div>
            </transition>
        </div>
    </div>
</template>
<script setup>
import { computed, ref } from 'vue';
import { MapPin, Map, ChevronDown, Earth } from 'lucide-vue-next';

const props = defineProps({
    dealerGroup: {
        type: Object,
        required: true
    }
});

const selectedDealer = defineModel({
    type: Object,
    default: () => ({})
});

const showAdditionalLocations = ref(false);

const getPrimaryLocationAddress = () => {
    if (!props.dealerGroup?.primaryLocation?.location?.address) {
        return '';
    }
    
    const address = props.dealerGroup.primaryLocation.location.address;
    const city = address.city || '';
    const state = address.state || '';
    const street = address.street || '';
    
    return `${city}, ${state}, ${street}`.replace(/^,\s*|,\s*$/, '').replace(/,\s*,/g, ',');
};

const getLocationAddress = (location) => {
    if (!location?.location?.address) {
        return '';
    }
    
    const address = location.location.address;
    const city = address.city || '';
    const state = address.state || '';
    const street = address.street || '';
    
    return `${city}, ${state}, ${street}`.replace(/^,\s*|,\s*$/, '').replace(/,\s*,/g, ',');
};

const setSelectedDealer = (dealer) => {
    selectedDealer.value = dealer;
};

const toggleAdditionalLocations = () => {
    showAdditionalLocations.value = !showAdditionalLocations.value;
};

// Enhanced location selection logic to handle dealers with multiple locations
// This prevents all locations from the same dealer being marked as active
const isLocationSelected = (location) => {
    if (!selectedDealer.value || !location) {
        return false;
    }

    // First check if dealerId matches
    const dealerIdMatches = selectedDealer.value.dealerId === location.dealerId;
    
    if (!dealerIdMatches) {
        return false;
    }

    // For more precise matching, we need to check a unique identifier for each location
    // We'll use a combination of location name, address, and coordinates
    const selectedLocation = selectedDealer.value.location;
    const currentLocation = location.location;
    
    if (!selectedLocation || !currentLocation) {
        return false;
    }

    // Create unique identifiers for comparison
    const selectedLocationId = createLocationId(selectedLocation);
    const currentLocationId = createLocationId(currentLocation);
    
    return selectedLocationId === currentLocationId;
};

// Helper function to create a unique identifier for each location
const createLocationId = (location) => {
    if (!location) return '';
    
    // Create a unique identifier using multiple location properties
    const name = location.name || '';
    const street = location.address?.street || '';
    const city = location.address?.city || '';
    const lat = location.address?.lat || '';
    const lng = location.address?.lng || '';
    
    return `${name}-${street}-${city}-${lat}-${lng}`;
};

</script>

<style scoped lang="scss">
.dealers-app {
    &__card {
        &--dealer-group {
            border: 1px solid #e5e5e5;
            border-bottom: none;

            &:last-child {
                border-bottom: 1px solid #e5e5e5;
            }
        }

        &--dealer {
            display: flex;
            flex-direction: column;
            padding: 1rem;
            background-color: #fff;
            border-radius: 0;
            cursor: pointer;
            transition: transform 0.2s;
            color: #101921;
            text-align: left;
            justify-content: flex-start;
            border: none;
            width: 100%;

            &:hover {
                background-color: #f8f9fa;

                .distance-badge {
                    color: #707070;
                }
            }

            .distance-badge {
                display: flex;
                align-items: center;
                gap: 2px;
                font-size: 16px;
                color: #707070;
                font-weight: 400;
            }

            &:active,
            &:focus {
                background-color: transparent;
            }

            &.active {
                background-color: #fef7f0;
                border-left: 3px solid #e0457b;

                .dealers-app__card--name {
                    color: #e0457b;
                }
            }

            &.online-retailer {
                background-color: #fffaf7;
                border-left: 3px solid #ff7f32;

                &:hover {
                    background-color: #fef7f0;
                }

                &.active {
                    background-color: #fff4ed;
                    border-left: 3px solid #ff7f32;

                    .dealers-app__card--name {
                        color: #ff7f32;
                    }
                }
            }
        }

        &--additional-locations {
            background-color: #f8f9fa;
            border-top: 1px solid #e5e5e5;
        }

        &--toggle-locations {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 0.75rem 1rem;
            background: none;
            border: none;
            cursor: pointer;
            font-size: 14px;
            color: #e0457b;
            font-weight: 500;
            width: 100%;
            text-align: left;
            transition: background-color 0.2s;

            &:hover {
                background-color: #e9ecef;
            }

            .rotated {
                transform: rotate(180deg);
                transition: transform 0.3s ease;
            }

            svg {
                transition: transform 0.3s ease;
                flex-shrink: 0;
            }
        }

        &--locations-list {
            border-top: 1px solid #dee2e6;
        }

        &--additional-location {
            display: flex;
            flex-direction: column;
            padding: 0.75rem 1rem 0.75rem 2rem;
            background-color: #fff;
            border: none;
            border-bottom: 1px solid #f1f3f4;
            cursor: pointer;
            color: #101921;
            text-align: left;
            width: 100%;
            transition: background-color 0.2s;

            &:hover {
                background-color: #f8f9fa;
            }

            &.active {
                background-color: #fef7f0;
                border-left: 3px solid #e0457b;
            }

            &:last-child {
                border-bottom: none;
            }

            .distance-badge {
                display: flex;
                align-items: center;
                gap: 2px;
                font-size: 14px;
                color: #707070;
                font-weight: 400;
                margin-bottom: 0.25rem;
            }
        }

        &--additional-location-name {
            font-size: 0.9rem;
            font-weight: 500;
            color: #333;
            margin: 0 0 0.25rem 0;
        }

        &--name {
            font-size: 1.25rem;
            font-weight: bold;
            margin-bottom: 0;
            margin-top: 0;
        }

        &--online-badge {
            margin: 0.25rem 0;
            
            span {
                background-color: #fff4ed;
                color: #ff7f32;
                padding: 4px 8px;
                border-radius: 12px;
                font-size: .8rem;
                font-weight: 500;
                border: 1px solid #ffb380;
                display: flex;
                align-items: center;
                gap: 5px;
                line-height: 0;
                width: fit-content;
            }
        }

        &--location-name {
            font-size: 0.95rem;
            color: #666;
            margin: 0.25rem 0 0.5rem 0;
            font-weight: 400;
        }

        &--distance {
            font-size: 1rem;
            color: #666;
            margin-bottom: 0.5rem;
        }

        &--address {
            display: flex;
            align-items: flex-start;
            gap: 3px;
            font-size: 1.1rem;
            color: #101921;
            padding: 0;
            margin: 1rem 0 0 0;
            font-weight: 300;

            .dealers-app__card--additional-location & {
                font-size: 0.95rem;
                margin: 0.25rem 0 0 0;
            }
        }
    }
}

/* Transition styles for collapsible */
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.3s ease;
    overflow: hidden;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    opacity: 0;
    max-height: 0;
}

.slide-fade-enter-to,
.slide-fade-leave-from {
    opacity: 1;
    max-height: 500px;
}
</style>